import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {playMono} from '../../../../components/AudioPlayer/monoPlayback';
import {releasePlayback} from '../../../../components/AudioPlayer/exclusivePlayback';
import {usePlaybackSpeed} from '../../../../hooks/usePlaybackSpeed';

const TICK_MS = 100;

export interface SeekOptions {
    play?: boolean;
}

export interface AudioPlayback {
    attachAudio: (element: HTMLAudioElement | null) => void;
    src: string | null;
    canSeek: boolean;
    currentTime: number;
    duration: number;
    playing: boolean;
    rate: number;
    cycleRate: () => void;
    play: () => void;
    pause: () => void;
    toggle: () => void;
    seek: (seconds: number, options?: SeekOptions) => void;
    skip: (delta: number) => void;
}

interface Options {
    initialTime?: number | null;
    fallbackDuration?: number;
}

const clamp = (value: number, max: number): number => Math.min(Math.max(0, value), max > 0 ? max : value);

export const useAudioPlayback = (src: string | null, {initialTime = null, fallbackDuration = 0}: Options = {}): AudioPlayback => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [element, setElement] = useState<HTMLAudioElement | null>(null);
    const attachAudio = useCallback((node: HTMLAudioElement | null) => {
        audioRef.current = node;
        setElement(node);
    }, []);
    const [currentTime, setCurrentTime] = useState(() => (src && initialTime && initialTime > 0 ? initialTime : 0));
    const [mediaDuration, setMediaDuration] = useState(0);
    const [playing, setPlaying] = useState(false);
    const pendingSeek = useRef<number | null>(initialTime && initialTime > 0 ? initialTime : null);
    const {rate, cycleRate} = usePlaybackSpeed(audioRef, element && src ? src : '');

    const duration = mediaDuration > 0 ? mediaDuration : fallbackDuration;

    useEffect(() => {
        const audio = element;
        if (!audio || !src) return;
        const sync = () => setCurrentTime(audio.currentTime);
        const onMetadata = () => {
            if (Number.isFinite(audio.duration)) setMediaDuration(audio.duration);
            if (pendingSeek.current != null) {
                audio.currentTime = clamp(pendingSeek.current, audio.duration);
                pendingSeek.current = null;
            }
            sync();
        };
        const onPlay = () => setPlaying(true);
        const onPause = () => {
            setPlaying(false);
            sync();
        };
        audio.addEventListener('loadedmetadata', onMetadata);
        audio.addEventListener('durationchange', onMetadata);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onPause);
        audio.addEventListener('seeked', sync);
        audio.addEventListener('timeupdate', sync);
        if (audio.readyState >= 1) onMetadata();
        return () => {
            audio.removeEventListener('loadedmetadata', onMetadata);
            audio.removeEventListener('durationchange', onMetadata);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onPause);
            audio.removeEventListener('seeked', sync);
            audio.removeEventListener('timeupdate', sync);
            audio.pause();
            releasePlayback(audio);
        };
    }, [element, src]);

    useEffect(() => {
        if (!playing) return;
        let frame = 0;
        let last = 0;
        const tick = (now: number) => {
            const audio = audioRef.current;
            if (audio && now - last >= TICK_MS) {
                last = now;
                setCurrentTime(audio.currentTime);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [playing]);

    const play = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !src) return;
        playMono(audio).catch(() => setPlaying(false));
    }, [src]);

    const pause = useCallback(() => {
        audioRef.current?.pause();
    }, []);

    const toggle = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !src) return;
        if (audio.paused) play();
        else audio.pause();
    }, [play, src]);

    const seek = useCallback((seconds: number, options: SeekOptions = {}) => {
        const audio = audioRef.current;
        if (!audio || !src) return;
        const known = Number.isFinite(audio.duration) && audio.duration > 0;
        if (!known && audio.readyState < 1) {
            pendingSeek.current = Math.max(0, seconds);
        } else {
            audio.currentTime = clamp(seconds, audio.duration);
        }
        setCurrentTime(Math.max(0, seconds));
        if (options.play) play();
    }, [play, src]);

    const skip = useCallback((delta: number) => {
        const audio = audioRef.current;
        if (!audio || !src) return;
        seek(audio.currentTime + delta);
    }, [seek, src]);

    return useMemo(() => ({
        attachAudio,
        src,
        canSeek: Boolean(src),
        currentTime,
        duration,
        playing,
        rate,
        cycleRate,
        play,
        pause,
        toggle,
        seek,
        skip,
    }), [attachAudio, src, currentTime, duration, playing, rate, cycleRate, play, pause, toggle, seek, skip]);
};
